import React, { useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Palette, Image as ImageIcon, Sparkles } from 'lucide-react';

const BackgroundSelector = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState('color');
  
  // Parse the current value to determine type
  const currentBg = value || { type: 'color', value: '#ffffff' };
  
  const solidColors = [
    '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6',
    '#000000', '#212529', '#343a40', '#495057',
    '#007bff', '#6610f2', '#6f42c1', '#e83e8c',
    '#dc3545', '#fd7e14', '#ffc107', '#28a745',
    '#20c997', '#17a2b8', '#6c757d', '#f8f9fa'
  ];
  
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)'
  ];

  const handleColorChange = (color) => {
    onChange({ type: 'color', value: color });
  };

  const handleGradientChange = (gradient) => {
    onChange({ type: 'gradient', value: gradient });
  };

  const handleImageChange = (e) => {
    const url = e.target.value;
    if (url) {
      onChange({ type: 'image', value: url });
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onChange({ type: 'image', value: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-slate-700">Slide Background</Label>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="color" className="gap-1">
            <Palette className="w-4 h-4" />
            Color
          </TabsTrigger>
          <TabsTrigger value="gradient" className="gap-1">
            <Sparkles className="w-4 h-4" />
            Gradient
          </TabsTrigger>
          <TabsTrigger value="image" className="gap-1">
            <ImageIcon className="w-4 h-4" />
            Image
          </TabsTrigger>
        </TabsList>

        <TabsContent value="color" className="space-y-3">
          <div className="grid grid-cols-10 gap-2">
            {solidColors.map((color) => (
              <button
                key={color}
                onClick={() => handleColorChange(color)}
                className={`w-8 h-8 rounded-md border-2 transition-all hover:scale-110 ${
                  currentBg.type === 'color' && currentBg.value === color
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-slate-300'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="color"
              value={currentBg.type === 'color' ? currentBg.value : '#ffffff'}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-20 h-10"
            />
            <Input
              type="text"
              value={currentBg.type === 'color' ? currentBg.value : '#ffffff'}
              onChange={(e) => handleColorChange(e.target.value)}
              placeholder="#ffffff"
              className="flex-1"
            />
          </div>
        </TabsContent>

        <TabsContent value="gradient" className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {gradients.map((gradient, index) => (
              <button
                key={index}
                onClick={() => handleGradientChange(gradient)}
                className={`h-16 rounded-md border-2 transition-all hover:scale-105 ${
                  currentBg.type === 'gradient' && currentBg.value === gradient
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-slate-300'
                }`}
                style={{ background: gradient }}
              />
            ))}
          </div>
          <Input
            type="text"
            value={currentBg.type === 'gradient' ? currentBg.value : ''}
            onChange={(e) => handleGradientChange(e.target.value)}
            placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            className="font-mono text-xs"
          />
        </TabsContent>

        <TabsContent value="image" className="space-y-3">
          <div className="space-y-2">
            <Input
              type="text"
              value={currentBg.type === 'image' ? currentBg.value : ''}
              onChange={handleImageChange}
              placeholder="Enter image URL"
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Or upload:</span>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="flex-1"
              />
            </div>
          </div>
          {currentBg.type === 'image' && currentBg.value && (
            <div className="relative h-32 rounded-md overflow-hidden border-2 border-slate-300">
              <img
                src={currentBg.value}
                alt="Background preview"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Preview */}
      <div className="mt-3">
        <Label className="text-xs text-slate-600">Preview</Label>
        <div
          className="mt-1 h-20 rounded-md border-2 border-slate-300"
          style={{
            background: currentBg.type === 'color' 
              ? currentBg.value 
              : currentBg.type === 'gradient'
              ? currentBg.value
              : currentBg.type === 'image'
              ? `url(${currentBg.value}) center/cover`
              : '#ffffff'
          }}
        />
      </div>
    </div>
  );
};

export default BackgroundSelector;
